import React, { useState, useEffect } from 'react';
import { MapPin, Clock, Users, Info, X, Search, ArrowRight } from 'lucide-react';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import WalletMenu from '../components/WalletMenu';
import { supabase } from '../lib/supabase';
import NavigationMenu from '../components/NavigationMenu';
import LuxuryCarModal from '../components/modals/LuxuryCarModal';

export interface LuxuryCar {
  id: string;
  name: string;
  brand: string;
  model: string;
  type: string;
  location: string;
  currency: string;
  price_per_day: number;
  price_per_hour: number | null;
  price_per_week: number | null;
  description: string | null;
  features: string[] | null;
  image_url: string | null;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

const LuxuryCars = () => {
  const [cars, setCars] = useState<LuxuryCar[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCar, setSelectedCar] = useState<LuxuryCar | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCars();
  }, [filterCategory, searchTerm]);

  const fetchCars = async () => {
    try {
      setLoading(true);
      let query = supabase.from('luxurycars').select('*');

      if (filterCategory !== 'all') {
        query = query.ilike('type', filterCategory);
      }

      if (searchTerm) {
        const term = `%${searchTerm.toLowerCase()}%`;
        query = query.or(
          `ilike(name, '${term}'),ilike(brand, '${term}'),ilike(model, '${term}'),ilike(location, '${term}')`
        );
      }

      const { data, error } = await query.limit(20);
      if (error) throw error;

      setCars(data || []);
    } catch (error) {
      console.error('Error fetching cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCarClick = (car: LuxuryCar) => {
    setSelectedCar(car);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedCar(null);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* HEADER */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-8xl mx-auto flex justify-between items-center px-6 py-4">
          <div className="flex gap-6 items-center">
            <Logo />
            <NavigationMenu />
          </div>
          <WalletMenu onShowDashboard={() => {}} />
        </div>
      </header>

      {/* MAIN */}
      <main className="flex-1 pt-[88px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* HERO */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Luxury Car Rental</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the thrill of driving the world’s most prestigious automobiles.
            </p>
          </div>

          {/* SEARCH */}
          <div className="max-w-md mx-auto mb-8 relative">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by brand, model, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black shadow-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* FILTER BUTTONS */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {[
              { key: 'all', label: 'All Cars' },
              { key: 'supercar', label: 'Supercars' },
              { key: 'luxury', label: 'Luxury Sedans' },
              { key: 'suv', label: 'Luxury SUVs' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilterCategory(key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterCategory === key
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* RESULTS */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-12 h-12 border-4 border-gray-300 border-t-black rounded-full animate-spin" />
            </div>
          ) : cars.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.map((car) => (
                <div
                  key={car.id}
                  className="bg-white rounded-xl shadow hover:shadow-lg transition cursor-pointer group border"
                  onClick={() => handleCarClick(car)}
                >
                  <div className="relative h-48">
                    <img
                      src={car.image_url || '/fallback.jpg'}
                      alt={car.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {car.is_featured && (
                      <span className="absolute top-3 left-3 bg-black text-white text-xs font-bold px-2 py-1 rounded">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-bold mb-1">{car.name}</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      <MapPin size={14} className="inline mr-1" />
                      {car.location}
                    </p>
                    <div className="flex justify-between text-sm text-gray-600 mb-2">
                      <span><Clock size={14} className="inline mr-1" />{car.model}</span>
                      <span><Users size={14} className="inline mr-1" />{car.brand}</span>
                    </div>
                    <div className="text-lg font-bold">
                      {car.currency} {car.price_per_day.toLocaleString()}/day
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Info size={24} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">No cars found</h3>
              <p className="text-gray-500 mt-2">
                No cars match your search. Try a different filter or spelling.
              </p>
            </div>
          )}

          {/* CTA */}
          <div className="bg-black text-white p-12 rounded-2xl text-center mt-16">
            <h2 className="text-3xl font-bold mb-4">Ready to Drive Luxury?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Book now and make your journey unforgettable.
            </p>
            <a
              href="mailto:bookings@privatecharterx.com"
              className="inline-flex items-center gap-2 bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-100"
            >
              Request a Quote <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </main>

      <Footer />

      {showModal && selectedCar && (
        <LuxuryCarModal car={selectedCar} onClose={closeModal} />
      )}
    </div>
  );
};

export default LuxuryCars;
